import { useRef, useState } from "react";
import { ApiError, REQUEST_TIMED_OUT, demoApi } from "@/api/client";
import { Icon } from "@/components/Icon";
import { downscaleForAnalysis } from "@/lib/receiptImage";
import { scanQueue } from "@/offline/scanQueue";
import { useScanQueue } from "@/hooks/useScanQueue";
import type { ReceiptAnalysis } from "@/api/types";
import "./ReceiptScan.css";

interface ReceiptScanProps {
  /**
   * Called with whatever the analyzer returned. The parent PREFILLS its existing
   * pickers from this rather than the scan owning a separate confirm flow — see
   * the note on corrections below.
   */
  onAnalyzed: (analysis: ReceiptAnalysis) => void;
  /**
   * Needed to hold an offline-captured photo for later replay (build tasks
   * #1/#2 — `offline/scanQueue.ts`). `null` while reference data is still
   * resolving; a scan attempted in that narrow window degrades to the error
   * state below rather than holding a photo it could never later attach to
   * an entity.
   */
  entityId: string | null;
}

type ScanState = "idle" | "scanning" | "error";

/**
 * "Scan a receipt" — the entry point to Stretch A (tradeshow-2026-09.md).
 *
 * WHY THIS PREFILLS THE NORMAL FORM instead of owning its own confirm screen:
 * the demo's whole design is that the model's first guess is allowed to be wrong,
 * because the visitor correcting it *is* the demo. Dropping the analysis into the
 * pickers that already exist means correcting a wrong guess is one tap on a
 * control the visitor has already seen, and whatever they end up submitting
 * becomes the confirmed mapping that makes the second scan instant. A separate
 * confirm UI would have to reimplement all of that and would make the correction
 * feel like an error path rather than the point.
 *
 * `capture="environment"` asks a phone for the REAR camera directly rather than a
 * file browser. Desktop browsers ignore it and show a file picker, which is what
 * we want when testing on a laptop.
 *
 * ── OFFLINE (build tasks #1/#2) ──────────────────────────────────────────────
 * A network failure here (`ApiError.status === 0` — see api/client.ts) must
 * never surface as a raw exception string ("Failed to fetch") to a stranger at
 * a booth. Instead the captured photo is held on-device (`scanQueue.ts`) and
 * read automatically the moment the app is back online, with no further tap —
 * there is nobody guaranteed to be looking at the screen when reconnect
 * happens, so this cannot wait for a confirmation the visitor may never give.
 *
 * ── THE PRIVACY LINE IS STATEFUL, AND IT WENT FALSE ──────────────────────────
 * It read "The photo isn't saved — we read it and let it go.", and that was
 * true when it was written: the server read the image and discarded it.
 *
 * ⚠️ ef-006 CHANGED THAT AND THE COPY DID NOT FOLLOW. The analyze handler now
 * calls SaveReceiptImageAsync — deliberately, because capture-and-attach is the
 * half of the receipt feature that needs no network and is what Christy actually
 * asked for (ADR 0007). So from that commit until 2026-09-06 the app told a
 * stranger, on the screen where they decide whether to photograph their own
 * receipt into someone else's datacentre, that it would not be kept. It was.
 *
 * That is not a stale comment; it is an untrue statement about someone's
 * personal data, made at the moment of collection. tradeshow-2026-09.md lists
 * "do not retain uploaded images, and say so on screen" as a requirement — the
 * retention decision changed, so the sentence had to.
 *
 * The offline copy was wrong in the same way: "it never goes anywhere else" was
 * true only while nothing persisted server-side. The local copy IS deleted after
 * upload (`scanQueue.ts` `drain`); the uploaded one is now kept.
 */
export function ReceiptScan({ onAnalyzed, entityId }: ReceiptScanProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<ScanState>("idle");
  const [error, setError] = useState<string | null>(null);
  const queuedScans = useScanQueue();

  /**
   * Reference data (and the entity it discovers) has not arrived yet, so a scan taken now
   * has nowhere to be filed. The offline branch below has always refused to hold a photo in
   * this state — "never hold a photo with nowhere to attach it later" — and the ONLINE path
   * had no such guard, which is the gap this closes.
   */
  const notReady = entityId === null;

  async function handleFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setState("scanning");
    setError(null);
    try {
      /*
        SHRINK BEFORE SENDING — and before queueing, so a held photo is small too.

        A full-resolution phone photo is REFUSED by the model outright: measured on
        2026-09-03, a 3024x4032 shot came back "max_tokens must be at least 1, got -3676"
        — it tokenises into more than the whole context window. Every visitor photo at the
        booth is that shape. It hid because the analyzer degrades politely (HTTP 200,
        nulls, Low), so a rejected request looks like "the scan found nothing".

        It also cuts a 4MB upload to a couple of hundred KB, which on a busy hall's
        cellular is the difference the visitor actually feels.
      */
      const image = await downscaleForAnalysis(file);
      onAnalyzed(await demoApi.analyzeReceipt(image, undefined, file.name || "receipt.jpg"));
      setState("idle");
    } catch (err) {
      /*
        HOLD THE PHOTO ONLY WHEN THE REQUEST PROVABLY NEVER LANDED.

        Was `err.status === 0` — offline alone. A 502 or 504 is the proxy saying it
        never reached the application, which is the same information state as offline
        and equally safe to hold; the visitor's photo was being dropped on the floor in
        both. `neverReachedTheServer` is that boundary, shared with the entry submit.

        The boundary matters MORE here than there. The queue's drain re-analyses the
        photo and CREATES AN ENTRY from it, so holding a request that did in fact reach
        the server would post the receipt twice. 0/502/504 each rule that out; a
        timeout does not, which is why it is excluded below and shown as an error
        instead — a demonstrated risk, not a theoretical one: the hung entry submit
        landed 40 seconds after the pause was lifted.
      */
      if (err instanceof ApiError && err.neverReachedTheServer) {
        if (entityId) {
          // The SHRUNK image, not the original: the drain re-analyses it later and would
          // hit the same refusal, and IndexedDB does not need the extra megabytes either.
          await scanQueue.enqueue(
            await downscaleForAnalysis(file),
            file.name || "receipt.jpg",
            entityId,
          );
          setState("idle");
        } else {
          // Rare: reference data (and the entity it discovers) is still
          // resolving. Never hold a photo with nowhere to attach it later.
          setState("error");
          setError("Can't save this photo right now — try again in a moment.");
        }
        return;
      }
      setState("error");
      setError(
        err instanceof ApiError && err.status === 413
          ? "That photo is too large. Try again — most phone cameras are fine."
          : err instanceof ApiError && err.status === REQUEST_TIMED_OUT
            ? "That took too long to read. Try the photo again."
            : /*
                A 5xx IS NOT A BAD PHOTOGRAPH, and this line said it was.

                MEASURED 2026-09-07 by stopping postgres under the visitor's phone — a
                single-host outage, which is what a booth runs on. The analyzer returned
                **500** and the stranger holding the receipt was told **"Could not read that
                receipt"**. Their receipt was fine. They are now looking at it, wondering if
                it is too crumpled, while the answer is that the database is down.

                It is the same mistake fixed hours earlier one layer up, where a scan taken
                before reference data had loaded was reported as "Couldn't fully read that
                receipt — missing business entity". Blaming the visitor's paper for the app's
                own state is a habit this screen has to be told out of twice.

                "In a moment" is kept because it is still the right instruction: the photo is
                NOT held on a 5xx (see the hold branch above — the request provably landed, so
                re-draining it could post the receipt twice), so trying again is genuinely
                what they should do.
              */
            err instanceof ApiError && err.status >= 500
              ? "EasyFarm couldn't take that just now — try again in a moment. "
                + "Nothing is wrong with your receipt."
              : /*
                  NOT `err.message`. An ApiError's message is the server's problem title,
                  so a 500 put "Internal Server Error" on a farmer's phone at a trade
                  show. Nothing in a failure response is written for the person holding
                  the receipt, and the raw string can also leak more about the backend
                  than a stranger at a booth should see.
                */
                "Could not read that receipt — try again in a moment.",
      );
    } finally {
      // Clear the input so picking the SAME file twice still fires a change event.
      // Without this, a visitor retrying after a failure gets no response at all.
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const hasPendingScans = queuedScans.length > 0;

  return (
    <div className="receipt-scan">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="receipt-scan__input"
        onChange={(e) => void handleFile(e.target.files?.[0])}
        // Hidden from the a11y tree because the button below is the real control;
        // exposing both would announce two things for one action.
        aria-hidden="true"
        tabIndex={-1}
      />

      {/*
        THE BEAT CANNOT START BEFORE THE APP CAN FINISH IT.

        `entityId` arrives with reference data, over the network, and is null until it does.
        A scan taken in that window is READ PERFECTLY and then thrown away: the analysis has
        vendor, category, amount and date, `ReceiptCapture` finds no entity to attach it to,
        and the visitor is told **"Couldn't fully read that receipt — missing business
        entity. Nothing was saved."** The photo was fine. The app was not ready, and it
        blamed the receipt.

        FOUND BY RUNNING IT, 2026-09-07, from a preserved failure snapshot — the flake that
        looked for a session like the success notice failing to render. Locally reference
        data resolves in about 5ms and the window is invisible; the first thing a visitor
        does at a booth is tap this button on a freshly-loaded page, on hall wifi.

        Disabling is the whole fix rather than better copy, because there is nothing to say:
        the wait is a fraction of a second and self-resolving. The label still reads "Scan a
        receipt" so the control never looks like a different feature — only the hint admits
        the wait, and `data-state` puts it where a test can read it (ui-breadcrumbs §1).
      */}
      <button
        type="button"
        className="receipt-scan__button"
        onClick={() => inputRef.current?.click()}
        disabled={state === "scanning" || notReady}
        aria-busy={state === "scanning"}
        data-state={state === "scanning" ? "scanning" : notReady ? "preparing" : "ready"}
      >
        <span className="receipt-scan__icon" aria-hidden="true">
          <Icon name="camera" size={21} />
        </span>
        <span className="receipt-scan__text">
          <span className="receipt-scan__label">
            {state === "scanning" ? "Reading your receipt…" : "Scan a receipt"}
          </span>
          <span className="receipt-scan__hint">
            {state === "scanning"
              ? "One moment"
              : notReady
                ? "Getting your farm ready…"
                : "Point your camera at it"}
          </span>
        </span>
      </button>

      {/*
        THE WAIT IS ANNOUNCED, not only drawn.

        The button disables itself and swaps its label to "Reading your receipt…", and
        neither reaches a screen reader: a disabled button loses focus, and a changed label
        is not a live region. A sighted visitor sees the state; a screen-reader user got
        silence.

        Survivable at the 250ms the hash stub takes. The real vision model takes about five
        seconds — measured at 4.9s on the built booth bundle — and five seconds of nothing
        after tapping a button reads as an app that has died. Wiring the real model turned a
        theoretical gap into a real one.

        role="status" is polite, not an alert: nothing failed, something is taking a moment.
        Visually hidden because the button already says it on screen; this exists to put the
        same words where assistive tech will read them.
      */}
      {state === "scanning" && (
        <p className="visually-hidden" role="status">
          Reading your receipt…
        </p>
      )}

      {state === "error" && error && (
        <p className="receipt-scan__error" role="status">
          {error}
        </p>
      )}

      {hasPendingScans && (
        <p className="receipt-scan__pending" role="status" data-state="pending">
          {queuedScans.length} photo{queuedScans.length === 1 ? "" : "s"} waiting to be
          read — we’ll do it automatically as soon as we can.
        </p>
      )}

      {/*
        Stated on the surface a stranger is looking at, not buried in a policy.
        They are photographing their own receipt into someone else's datacentre,
        and it is KEPT — see SaveReceiptImageAsync in the analyze handler, and the
        file header for how this sentence came to say the opposite for two days.
        See the header too for why this line changes while a photo is held.

        IT NO LONGER CLAIMS THE VISITOR IS OFFLINE, and that is a correctness fix rather
        than a wording preference. Both strings are driven by `hasPendingScans` — nothing
        here consults connectivity at all — so every reason for holding a photo produced
        "Offline right now". MEASURED 2026-09-06 by 502-ing the analyze endpoint with the
        network perfectly healthy: `navigator.onLine === true` while the screen said
        "Offline right now" and "once you're back online".
        
        That is the booth's likelier failure, not a hypothetical: of-002b exists because
        the API behind Caddy died while the hotspot was fine, and it happened twice more
        while this session was running. A visitor on full bars is told the problem is
        theirs; they check their phone, see a good signal, and conclude the app is lying.
        The app cannot tell whose side the fault is on, so it should not say.
      */}
      <p className="receipt-scan__privacy">
        {hasPendingScans
          ? "Until we can read it, this photo stays on your phone. It is then sent and kept with the entry, so you can open the receipt later."
          : "The photo is kept with the entry, so you can open the receipt later."}
      </p>
    </div>
  );
}
